import React from "react";
import { Edit3, Lock } from "lucide-react";
import { UnitToggle, TextField, NumField, NotesField, u } from "./_shared";
import { Field, TextInput } from "@/components/ReportPrimitives";
import { deriveMudGradient, deriveMudSg } from "@/lib/calc/mudAutoCalc";

// MudProfileForm — single source of truth for the mud system.
//
// Auto-calc (per acceptance pass): gradient & SG derive from mud weight.
// See lib/calc/mudAutoCalc.js for the formulas. Each derived field has an
// Override toggle to allow manual entry; toggling back to Auto recomputes.

function _num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}
function _round(v, digits) {
  if (v == null) return "";
  const m = Math.pow(10, digits);
  return String(Math.round(v * m) / m);
}

export default function MudProfileForm({ value, onChange }) {
  const units = value.units || "IMP";
  const set = (patch) => onChange({ ...value, ...patch });

  // Auto/manual flags (default to auto if unset, including legacy payloads).
  const gradAuto = value._gradientManual !== true;
  const sgAuto   = value._sgManual !== true;

  const derivedGrad = deriveMudGradient(value.density, units);
  const derivedSg   = deriveMudSg(value.density, units);

  const gradDisplay = gradAuto && derivedGrad != null
    ? _round(derivedGrad, 4) : (value.gradient ?? "");
  const sgDisplay = sgAuto && derivedSg != null
    ? _round(derivedSg, 4)   : (value.sg ?? "");

  // When density changes in auto mode, persist the derived values so the
  // snapshot stored on a kill sheet carries gradient that matches density
  // (composer reads `session.mud.gradient` directly).
  const setDensity = (v) => {
    const patch = { density: v };
    if (gradAuto) {
      const g = deriveMudGradient(v, units);
      patch.gradient = g == null ? "" : _round(g, 4);
    }
    if (sgAuto) {
      const s = deriveMudSg(v, units);
      patch.sg = s == null ? "" : _round(s, 4);
    }
    set(patch);
  };

  const setUnits = (newUnits) => {
    const patch = { units: newUnits };
    if (gradAuto) {
      const g = deriveMudGradient(value.density, newUnits);
      if (g != null) patch.gradient = _round(g, 4);
    }
    if (sgAuto) {
      const s = deriveMudSg(value.density, newUnits);
      if (s != null) patch.sg = _round(s, 4);
    }
    set(patch);
  };

  const toggleGradAuto = () => {
    if (!gradAuto) {
      const g = deriveMudGradient(value.density, units);
      set({ _gradientManual: false, gradient: g == null ? "" : _round(g, 4) });
    } else {
      set({ _gradientManual: true });
    }
  };
  const toggleSgAuto = () => {
    if (!sgAuto) {
      const s = deriveMudSg(value.density, units);
      set({ _sgManual: false, sg: s == null ? "" : _round(s, 4) });
    } else {
      set({ _sgManual: true });
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="mud-profile-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TextField value={value} onChange={onChange} field="label" label="Profile label"
                   placeholder="e.g. 11.8 ppg WBM · Build #4" testId="mud-label" />
        <TextField value={value} onChange={onChange} field="rig" label="Rig (optional)"
                   placeholder="e.g. Rig Alpha" testId="mud-rig" />
        <UnitToggle value={units} onChange={setUnits} testId="mud-units" />
      </div>

      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Drilling-fluid properties
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label={`Density (${u.density(units)})`}
                 helper="Enter mud weight. Gradient and SG derive from this value.">
            <TextInput type="number" value={value.density ?? ""} onChange={setDensity}
                       testId="mud-density" />
          </Field>

          <DerivedField label={`Gradient (${u.gradient(units)})`}
                        auto={gradAuto} onToggle={toggleGradAuto}
                        value={gradDisplay}
                        onChange={(v) => set({ gradient: v })}
                        testId="mud-gradient" />

          <DerivedField label="SG"
                        auto={sgAuto} onToggle={toggleSgAuto}
                        value={sgDisplay}
                        onChange={(v) => set({ sg: v })}
                        testId="mud-sg" />
        </div>
      </fieldset>

      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Active surface volume
        </legend>
        <NumField value={value} onChange={onChange} field="activeSurfaceVolume"
                  label="Active surface system volume" units={units} kind="volume" testId="mud-activeVol"
                  helper="Used by the IWCF Kill Sheet to compute surface-line strokes." />
      </fieldset>

      <NotesField value={value} onChange={onChange} testId="mud-notes" />
    </div>
  );
}

// DerivedField — numeric field with an Auto/Override badge.
// In Auto mode the input is read-only and shows the live derived value;
// clicking Override flips to manual entry. Clicking Auto snaps the value
// back to the derived calculation.
function DerivedField({ label, auto, onToggle, value, onChange, testId }) {
  return (
    <Field
      label={label}
      helper={auto ? "Auto — derived from Density." : "Manual override. Click Auto to recompute."}
    >
      <div className="flex items-stretch gap-2">
        <div className="flex-1">
          <TextInput
            type="number"
            value={value ?? ""}
            onChange={onChange}
            readOnly={auto}
            testId={testId}
          />
        </div>
        <button type="button" onClick={onToggle}
                data-testid={`${testId}-toggle`}
                title={auto ? "Switch to manual override" : "Reset to auto-derive"}
                className="text-[10px] px-2 rounded-md whitespace-nowrap inline-flex items-center gap-1"
                style={{
                  minHeight: 36,
                  background: auto ? "rgba(245,180,0,0.10)" : "rgba(22,24,29,0.6)",
                  color: auto ? "var(--rs-amber)" : "var(--rs-text-2)",
                  border: `1px solid ${auto ? "rgba(245,180,0,0.35)" : "var(--rs-border)"}`,
                }}>
          {auto ? (<><Lock className="w-3 h-3" /> Auto</>) : (<><Edit3 className="w-3 h-3" /> Override</>)}
        </button>
      </div>
    </Field>
  );
}
